import { computed, Injectable, OnDestroy, signal, Signal, WritableSignal, inject } from '@angular/core';
import { filter, forkJoin, Subscription, finalize, of, take } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { CardsApiService } from '../cards-service/cards-api/cards-api.service';
import { Card, CardLimits, CardStatus } from '../../_interfaces/cards.interface';
import { Store } from '@ngxs/store';
import { SubscriptionSelectors } from '../../store/subscription/subscription.selectors';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class CardTopupsService implements OnDestroy {
  readonly router = inject(Router);
  private readonly store = inject(Store);
  protected cardsApiService = inject(CardsApiService);

  hasSubscriptionCards$ = this.store.select(SubscriptionSelectors.hasSubscriptionCards);
  readonly canFetchSubscriptionCards = toSignal(this.hasSubscriptionCards$, { initialValue: false });

  readonly subscriptions = new Subscription();
  public isLoading = signal(false);
  public conditionalReturnUrl = signal('/card-topups/scheduled');
  public cardLimits: WritableSignal<CardLimits | null> = signal(null);
  public virtualCards: WritableSignal<Card[]> = signal([]);
  public subscriptionCards: WritableSignal<Card[]> = signal([]);
  public cardsList: Signal<Card[]> = computed<Card[]>(() => {
    const isAvailable = (card: Card) => card.status !== CardStatus.Blocked && card.status !== CardStatus.Removed;
    return [...this.virtualCards().filter(isAvailable), ...this.subscriptionCards().filter(isAvailable)];
  });

  readonly createImmediateUrl = '/card-topups/immediate/add';
  readonly createRecurringUrl = '/card-topups/recurring/add';
  readonly createScheduledUrl = '/card-topups/scheduled/add';
  readonly createEmployeeUrl = '/add/employee-details';
  readonly editEmployeeUrl = '/employees/edit';
  readonly editInvitationUrl = '/employee-invitations/edit';
  readonly createSubCardUrl = '/cards/subscription-cards/create';
  readonly editRecurringUrl = '/card-topups/recurring/edit';
  readonly editScheduledUrl = '/card-topups/scheduled/edit';

  constructor() {
    // initial load
    this.getCardLimits();
    this.refreshCardData();

    // react to route changes
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.handleRouteChange(event.urlAfterRedirects);
        }),
    );
    this.handleRouteChange(this.router.url);

    // needed to read the updated state
    toObservable(this.canFetchSubscriptionCards)
      .pipe(
        filter((hasAccess) => hasAccess),
        take(1),
      )
      .subscribe(() => {
        this.refreshCardData();
      });
  }

  private handleRouteChange(url: string): void {
    this.conditionalReturnUrl.set(url.includes('recurring') ? '/card-topups/recurring' : '/card-topups/scheduled');

    // Refresh api call when these specific pages are loaded to ensure the newest data
    if (
      url.includes(this.createRecurringUrl) ||
      url.includes(this.createScheduledUrl) ||
      url.includes(this.createImmediateUrl) ||
      url.includes(this.createSubCardUrl)
    ) {
      this.refreshCardData();
      this.getCardLimits();
    }

    if (
      url.includes(this.editRecurringUrl) ||
      url.includes(this.editScheduledUrl) ||
      url.includes(this.createEmployeeUrl) ||
      url.includes(this.editEmployeeUrl) ||
      url.includes(this.editInvitationUrl)
    ) {
      this.getCardLimits();
    }
  }

  public getCardLimits(): void {
    this.subscriptions.add(
      this.cardsApiService.getCardLimits().subscribe({
        next: (limits) => {
          this.cardLimits.set(limits);
        },
      }),
    );
  }

  public refreshCardData(): void {
    this.isLoading.set(true);
    // Define our requests. If no access, we mock an empty response for subscriptions.
    const personalRequest$ = this.cardsApiService.getAllPersonalCards();
    const subscriptionRequest$ = this.canFetchSubscriptionCards()
      ? this.cardsApiService.getAllSubscriptionCards()
      : of([]); // Returns an empty array immediately without an API call

    this.subscriptions.add(
      forkJoin([personalRequest$, subscriptionRequest$])
        .pipe(finalize(() => this.isLoading.set(false)))
        .subscribe({
          next: ([personal, subscription]) => {
            this.virtualCards.set(personal);
            this.subscriptionCards.set(subscription);
          },
          error: () => {
            // Reset lists on error to avoid stale data
            this.virtualCards.set([]);
            this.subscriptionCards.set([]);
          },
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
